import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  X,
  Copy,
  Check,
  RefreshCw,
  Lightbulb,
  ArrowRight,
  Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * ContentPolicyModal - Displays helpful information when a video fails due to content policy
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - analysis: object from backend with flagged_keywords, suggested_prompt, tips
 * - onRetryWithSuggestion: (suggestedPrompt: string) => void
 * - onEditPrompt: () => void
 */
const ContentPolicyModal = ({
  isOpen,
  onClose,
  analysis,
  onRetryWithSuggestion,
  onEditPrompt
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !analysis) return null;

  const {
    flagged_keywords = [],
    keyword_alternatives = {},
    suggested_prompt = '',
    tips = [],
    severity = 'medium',
    message = ''
  } = analysis;

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(suggested_prompt);
      setCopied(true);
      toast.success("Suggested prompt copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const severityColors = {
    low: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    medium: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
    high: 'text-red-400 bg-red-400/10 border-red-400/30',
    unknown: 'text-blue-400 bg-blue-400/10 border-blue-400/30'
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#16162a] border border-orange-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="content-policy-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-orange-500/20 bg-orange-500/5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Shield className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Content Policy Notice</h2>
                <p className="text-sm text-orange-300/80">Your prompt needs adjustment</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="content-policy-close"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-5 overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Explanation */}
            <div className={`p-4 rounded-xl border ${severityColors[severity]}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Video generation was blocked by AI safety filters</p>
                  <p className="text-sm opacity-80 mt-1">{message}</p>
                </div>
              </div>
            </div>

            {/* Flagged Keywords */}
            {flagged_keywords.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  Flagged Keywords & Alternatives
                </h3>
                <div className="space-y-2">
                  {flagged_keywords.map((keyword, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10"
                    >
                      <span className="text-red-400 line-through font-mono text-sm px-2 py-1 bg-red-400/10 rounded">
                        {keyword}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-500" />
                      <div className="flex flex-wrap gap-2">
                        {(keyword_alternatives[keyword] || []).slice(0, 3).map((alt, altIdx) => (
                          <span 
                            key={altIdx}
                            className="text-green-400 font-mono text-sm px-2 py-1 bg-green-400/10 rounded cursor-pointer hover:bg-green-400/20 transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText(alt);
                              toast.success(`Copied: ${alt}`);
                            }}
                            title="Click to copy"
                          >
                            {alt}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Prompt */}
            {suggested_prompt && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-green-400" />
                  Suggested Prompt (Click to copy)
                </h3>
                <div 
                  className="relative p-4 bg-green-500/5 border border-green-500/20 rounded-xl cursor-pointer hover:bg-green-500/10 transition-colors group"
                  onClick={handleCopyPrompt}
                >
                  <p className="text-sm text-gray-300 whitespace-pre-wrap pr-10">
                    {suggested_prompt}
                  </p>
                  <div className="absolute top-3 right-3">
                    {copied ? (
                      <Check className="w-5 h-5 text-green-400" />
                    ) : (
                      <Copy className="w-5 h-5 text-gray-500 group-hover:text-green-400 transition-colors" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            {tips.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-blue-400" />
                  Tips to Avoid Content Flags
                </h3>
                <ul className="space-y-2">
                  {tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="text-blue-400 mt-1">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-5 border-t border-orange-500/20 bg-black/20">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              Close
            </Button>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  handleCopyPrompt();
                  onEditPrompt?.();
                }}
                className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                data-testid="copy-and-edit-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy & Edit
              </Button>
              
              {suggested_prompt && onRetryWithSuggestion && (
                <Button
                  onClick={() => onRetryWithSuggestion(suggested_prompt)}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
                  data-testid="retry-with-suggestion-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry with Suggestion
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContentPolicyModal;
